import { useCallback, useRef, useState } from 'react';
import Axios from 'axios';
import {
  plato_api_generate_validation_report_assessment_path,
  plato_api_validation_report_assessment_path,
} from '@/modules/routes';

interface QuestionValidation {
  has_image: boolean;
  question_number: number;
  rationale: string;
  verdict: 'Valid' | 'Invalid' | 'Incorrect' | 'Vague';
}

interface ValidationSummary {
  incorrect_count: number;
  invalid_count: number;
  total_questions: number;
  vague_count: number;
  valid_count: number;
}

export interface ValidationReport {
  assessmentTitle?: string;
  builderUrl?: string;
  generatedAt?: string;
  modelName?: string;
  questions?: QuestionValidation[];
  summary?: ValidationSummary;
}

interface ValidationResponse {
  assessment_title?: string;
  builder_url?: string;
  error?: string;
  generated_at?: string;
  model_name?: string;
  report?: string;
}

const useAssessmentValidator = () => {
  const currentAssessmentIdRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ValidationReport | null>(null);

  const setReportFromResponse = (data: ValidationResponse) => {
    if (data.report) {
      const parsedReport: ValidationReport = JSON.parse(data.report);
      setReport({
        ...parsedReport,
        assessmentTitle: data.assessment_title,
        builderUrl: data.builder_url,
        generatedAt: data.generated_at,
        modelName: data.model_name,
      });
      return true;
    }
    return false;
  };

  const postReport = (assessmentId: string, revalidate = false) => {
    const route = plato_api_generate_validation_report_assessment_path({ id: assessmentId });

    Axios.post<ValidationResponse>(route, revalidate ? { revalidate: true } : {})
      .then((response) => {
        if (!setReportFromResponse(response.data)) {
          setError('No report was returned. Please try again.');
        }
      })
      .catch(err => setError(err.response?.data?.error || 'An unexpected error occurred. Please try again.'))
      .finally(() => setLoading(false));
  };

  const fetchReport = useCallback((assessmentId: string) => {
    currentAssessmentIdRef.current = assessmentId;
    setLoading(true);
    const route = plato_api_validation_report_assessment_path({ id: assessmentId });

    Axios.get<ValidationResponse>(route)
      .then(response => setReportFromResponse(response.data))
      .catch((err) => {
        if (err.response?.status !== 404) {
          setError('Failed to load existing report. Please submit again.');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const validate = (assessmentId: string) => {
    setError(null);
    setReport(null);
    setLoading(true);
    currentAssessmentIdRef.current = assessmentId;
    postReport(assessmentId);
  };

  const revalidate = () => {
    if (!currentAssessmentIdRef.current) return;

    setError(null);
    setReport(null);
    setLoading(true);
    postReport(currentAssessmentIdRef.current, true);
  };

  const clearReport = () => {
    currentAssessmentIdRef.current = null;
    setReport(null);
    setError(null);
  };

  return { clearReport, error, fetchReport, loading, report, revalidate, validate };
};

export default useAssessmentValidator;
